import type Immutable from 'immutable';
import { $getRoot } from 'lexical';

import type { LanguageEnum } from 'js/src/client';
import {
    getAIAvatarById,
    getAIAvatarsOrm,
    getAIAvatarVoicePresetById,
    getAIAvatarVoicePresetsOrm,
} from 'js/src/data/ai-voices-and-avatars';
import { getDashboardFlow } from 'js/src/data/app/app-selectors';
import type { ScriptComposerState } from 'js/src/data/script-composer/script-composer-reducers';
import type {
    DOC_TO_SCRIPT_PAGE,
    DocToScriptData,
    IdeaOutlineValueSet,
    ImmutableIdeaOutlineValueSet,
    Script,
    ScriptComposerData,
    ScriptMap,
    ScriptOption,
    ScriptOptionDurationValue,
    ScriptOptionsMap,
} from 'js/src/data/script-composer/script-composer-types';
import { getSentencesFromScript } from 'js/src/data/script-composer/script-composer-utils';
import { SCRIPT_CREATION_SOURCES } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

function getScriptComposer(state: RootState): ScriptComposerState {
    return state.script_composer;
}

export function getCurrentAIVoiceId(state: RootState) {
    return getScriptComposer(state).currentAIVoice;
}

export function getDocToScriptImportType(state: RootState): SCRIPT_CREATION_SOURCES | null {
    return getScriptComposer(state).docToScriptImportType;
}

export function getCurrentDocToScriptPage(state: RootState): DOC_TO_SCRIPT_PAGE | null {
    return getScriptComposer(state).docToScriptPage;
}

export function getAIScriptData(state: RootState): DocToScriptData {
    return getScriptComposer(state).docToScriptData;
}

export function getShouldSkipAISummarization(state: RootState): boolean {
    return getAIScriptData(state).shouldSkipAISummarization ?? false;
}

export function getScript(state: RootState, scriptOptionId: string | null): Script | null {
    if (!scriptOptionId) {
        return null;
    }
    if (!getScriptComposer(state).scriptCache?.get(scriptOptionId)) {
        return null;
    }
    return getScriptComposer(state).scriptCache.get(scriptOptionId, null);
}

function getScriptCache(state: RootState): Immutable.Map<string, Script> {
    return getScriptComposer(state).scriptCache;
}

export function usedScriptComposer(state: RootState): boolean {
    /**
     * We want to know for analytics purposes if the user used the script composer (return true)
     * or if they used "use all sentences) (return false). We can tell this by checking if the
     * script cache has any entries. A bit of a hack but works since the cache is only used by the Script Composer.
     */
    return getScriptCache(state)?.size > 0;
}

export function getIsScriptOptionSummaryLoading(
    state: RootState,
    scriptOptionId?: string,
): boolean {
    if (!scriptOptionId) {
        return false;
    }
    const isFetchingScriptOptionAIScript =
        getScriptComposer(state).isFetchingScriptOptionAIScript?.get(scriptOptionId) ?? false;
    if (!isFetchingScriptOptionAIScript) {
        return false;
    }
    return <boolean>isFetchingScriptOptionAIScript;
}

function getDocToScriptOptions(state: RootState): Immutable.Map<string, ScriptOption[]> {
    return getScriptComposer(state).docToScriptOptions;
}

export function getAIScriptOptionsData(
    state: RootState,
    scriptId: string | null,
): ScriptOption[] | null {
    if (!scriptId) {
        return null;
    }
    if (!getDocToScriptOptions(state).get(scriptId)) {
        return null;
    }
    return getDocToScriptOptions(state).get(scriptId) ?? null;
}

export function getEditedScript(state: RootState): Script | null {
    return getScriptComposer(state).editedScript;
}

export function getEditedScriptSentences(state: RootState): string[] {
    const editedScript = getEditedScript(state);
    if (!editedScript) {
        throw new Error('No edited script found');
    }
    return getSentencesFromScript(editedScript);
}

export function getEditedScriptInitialWordCount(state: RootState): number {
    return getScriptComposer(state).editedScriptInitialWordCount;
}

export function getEditedScriptLanguage(state: RootState): LanguageEnum | null {
    return getScriptComposer(state).editedScriptLanguage;
}

export function getIsFetchingAIScriptOptions(state: RootState, scriptId: string): boolean {
    const isFetchingAIScriptOptions =
        getScriptComposer(state).isFetchingAIScriptOptions?.get(scriptId);
    if (!isFetchingAIScriptOptions) {
        return false;
    }
    return <boolean>isFetchingAIScriptOptions;
}

export function getIsFetchingScriptFromIdea(state: RootState) {
    return getScriptComposer(state).isFetchingScriptFromIdea;
}

export function getSelectedScriptOption(state: RootState): ScriptOption | null {
    return getScriptComposer(state).selectedScriptOption;
}

export function getLongestSupportedScriptDuration(state: RootState): number | null {
    return getScriptComposer(state).longestSupportedScriptDuration;
}

/*
 * This is a helper function to get all the data needed to persist the script composer.
 */
export function getScriptComposerData(state: RootState): ScriptComposerData {
    const scriptCache: ScriptMap = {};
    const scriptOptions: ScriptOptionsMap = {};
    getScriptCache(state).forEach((value, key) => {
        scriptCache[key] = value;
    });
    getDocToScriptOptions(state).forEach((value, key) => {
        scriptOptions[key] = value;
    });
    const page = getCurrentDocToScriptPage(state);
    const shouldSkipAISummarization = getShouldSkipAISummarization(state);
    const editedScript = getEditedScript(state);
    const longestSupportedDuration = getLongestSupportedScriptDuration(state);
    const selectedScriptOption = getSelectedScriptOption(state);
    const editedScriptInitialWordCount = getEditedScriptInitialWordCount(state);
    const editedScriptLanguage = getEditedScriptLanguage(state);
    const selectedAIAvatarVoicePreset = getScriptComposer(state).currentAIAvatarVoicePreset;
    const selectedAIAvatar = getScriptComposer(state).currentAIAvatar;
    const selectedAIVoice = getScriptComposer(state).currentAIVoice;
    const importType = getScriptComposer(state).docToScriptImportType;
    const dashboardFlow = getDashboardFlow(state);
    const ideaOutlineData = getIdeaOutlineData(state).toJS() as unknown as IdeaOutlineValueSet;

    const data: ScriptComposerData = {
        page,
        script_options: scriptOptions,
        script_cache: scriptCache,
        skip_ai_summarization_stage: shouldSkipAISummarization,
        longest_supported_script_duration: longestSupportedDuration,
        edited_script_initial_word_count: editedScriptInitialWordCount,
        edited_script_language: editedScriptLanguage,
        selected_ai_avatar_voice_preset: selectedAIAvatarVoicePreset,
        selected_ai_avatar: selectedAIAvatar,
        selected_ai_voice: selectedAIVoice,
        import_type: importType,
        idea_outline_data: ideaOutlineData,
        dashboard_flow: dashboardFlow,
        is_converting_to_voiceover: false, // we don't want to persist this state
    };
    if (editedScript) {
        data['edited_script'] = editedScript;
    }
    if (selectedScriptOption) {
        data['selected_script_option'] = selectedScriptOption;
    }
    return data;
}

export function getScriptSentences(state: RootState): string[] {
    return getScriptComposer(state).scriptSentences;
}

export function getReferenceMaterial(state: RootState): string {
    const docToScriptImportType = getDocToScriptImportType(state);
    if (docToScriptImportType === SCRIPT_CREATION_SOURCES.IDEA) {
        const ideaOutlineData = getIdeaOutlineData(state);
        return `${ideaOutlineData.get('subject')}\n${ideaOutlineData.get('references').join('\n')}`;
    }

    // try script sentences
    const scriptSentences = getScriptSentences(state);
    if (scriptSentences.length > 0) {
        return scriptSentences.join('\n');
    }

    // try editedEditorState
    const editorState = getScriptComposer(state).docToScriptData.editedEditorState;
    if (editorState) {
        let editorText = '';
        editorState.read(() => {
            editorText = $getRoot().getTextContent();
        });
        if (editorText) {
            return editorText;
        }
    }

    // try originalEditorState
    const originalEditorState = getScriptComposer(state).docToScriptData.originalEditorState;
    if (originalEditorState) {
        let editorText = '';
        originalEditorState.read(() => {
            editorText = $getRoot().getTextContent();
        });
        if (editorText) {
            return editorText;
        }
    }

    return '';
}

export const getCurrentAIAvatarVoicePresetId = (state: RootState): number | undefined => {
    const currentPresetId = getScriptComposer(state).currentAIAvatarVoicePreset;
    const presetOrm = getAIAvatarVoicePresetsOrm(state);
    if (currentPresetId && !!presetOrm.getByIdOrUndefined(currentPresetId)) {
        return currentPresetId;
    }
    return presetOrm.getIdByIndexOrUndefined(0);
};

export function getCurrentAIAvatarVoicePreset(state: RootState) {
    const currentPresetId = getCurrentAIAvatarVoicePresetId(state);
    if (!currentPresetId) {
        return null;
    }
    return getAIAvatarVoicePresetById(state, currentPresetId);
}

export const getCurrentAIAvatarId = (state: RootState) => {
    const currentAIAvatarId = getScriptComposer(state).currentAIAvatar;
    const avatarOrm = getAIAvatarsOrm(state);
    if (currentAIAvatarId && !!avatarOrm.getById(currentAIAvatarId)) {
        return currentAIAvatarId;
    }
    return avatarOrm.getIdByIndexOrUndefined(0);
};

export function getCurrentAIAvatar(state: RootState) {
    const currentAvatarId = getCurrentAIAvatarId(state);
    if (!currentAvatarId) {
        return null;
    }
    return getAIAvatarById(state, currentAvatarId);
}

export function getIdeaOutlineData(state: RootState): ImmutableIdeaOutlineValueSet {
    return getScriptComposer(state).ideaOutlineData;
}

export function getIsUploadingScriptImages(state: RootState): boolean {
    return getScriptComposer(state).isUploadingScriptImages;
}

export function getTargetDuration(state: RootState): ScriptOptionDurationValue | null {
    return getScriptComposer(state).targetDuration;
}

export function getGroundhogAnalysis(state: RootState) {
    return getScriptComposer(state).groundhogAnalysis;
}

export function getSelectedBlockText(state: RootState): string | null {
    return getScriptComposer(state).selectedBlockText;
}

export function getIsConvertingToVoiceover(state: RootState): boolean {
    return getScriptComposer(state).isConvertingToVoiceover;
}

export function getIsGroundhogSupported(state: RootState): boolean {
    const importType = getDocToScriptImportType(state);
    if (importType === SCRIPT_CREATION_SOURCES.IDEA) {
        return false; // Groundhog is not supported for idea-to-script
    }
    const shouldSkipAISummarization = getShouldSkipAISummarization(state);
    if (shouldSkipAISummarization) {
        return false; // Groundhog is not supported if using "Skip and use all content"
    }
    const editedScript = getEditedScript(state);
    const referenceMaterial = getReferenceMaterial(state);
    if (!editedScript || !referenceMaterial) {
        return false; // Groundhog requires both edited script and reference material
    }
    if (editedScript.length === 0 || referenceMaterial.length === 0) {
        return false; // Groundhog requires non-empty edited script and reference material
    }
    return true;
}

export function getGroundhogAnalysisForSelectedBlock(state: RootState) {
    const selectedBlockText = getSelectedBlockText(state);
    if (!selectedBlockText) {
        return null;
    }
    const analysis = getScriptComposer(state).groundhogAnalysis?.script_analysis.find(
        (analysis) => analysis.sentence_text === selectedBlockText,
    );
    return analysis ?? null;
}
